class FreeShippingMeter extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.freeShippingEligible = 0;
        this.shipVal = "Free";

        this.progressBar = this.querySelector('[data-shipping-progress]');
        this.messageElement = this.querySelector('[data-shipping-message]');
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');

        if (!this.progressBar || !this.messageElement || !this.progressMeter) {
            console.error('Free shipping component: Required DOM elements not found');
            return;
        }

        this.textEnabled = this.progressBar.dataset.textEnabled === 'true';

        // data-threshold: free-shipping amount in cents of the shop currency (9900 = $99.00).
        // Other currencies: converted with Shopify's rate, rounded up to a whole unit.
        // data-current: cart total in cents of the CUSTOMER's currency.
        this.baseThreshold = parseInt(this.dataset.threshold) || 9900;
        this.currentTotal = parseInt(this.dataset.current) || 0;
        this.baseCurrency = this.dataset.baseCurrency || 'USD';

        // Wording comes from the "Free Shipping Message" setting ($X = amount in the customer's currency).
        this.achievedMessage = this.dataset.achievedMessage || "🎉 You've earned FREE shipping!";
        this.needsMoreMessage = this.dataset.needsMoreMessage || "You're $X away from FREE shipping!";

        this.addEventListener('change', this.onCartChange.bind(this));

        document.addEventListener('cart:updated', (event) => {
            if (event.detail && event.detail.cartData) {
                this.updateFromCartData(event.detail.cartData);
            }
        });

        // Paint right away from the server's cart total, then refresh from the live cart.
        this.calculateProgress({ total_price: this.currentTotal });
        this.initialize();
        FreeShippingMeter.watchSavings();
    }

    initialize() {
        fetch('/cart.js')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch cart data');
                }
                return response.json();
            })
            .then(cart => {
                this.cart = cart;
                this.calculateProgress(cart);
            })
            .catch(error => {
                console.error('Error fetching cart data:', error);
                this.calculateProgress({ total_price: this.currentTotal });
            });
    }

    onCartChange(e) {
        this.initialize();
    }

    updateFromCartData(cartData) {
        if (cartData && cartData.total_price !== undefined) {
            this.calculateProgress(cartData);
        }
    }

    // $99 in the shop currency; other currencies: $99 x Shopify's rate, rounded UP to a whole unit.
    getCurrencyInfo(cart) {
        const shopifyCurrency = (window.Shopify && window.Shopify.currency) || {};
        const currency = (cart && cart.currency) || shopifyCurrency.active || this.baseCurrency;
        let thresholdCents = this.baseThreshold;
        if (currency !== this.baseCurrency) {
            const rate = parseFloat(shopifyCurrency.rate);
            if (shopifyCurrency.active === currency && rate > 0) {
                thresholdCents = Math.ceil((this.baseThreshold / 100) * rate) * 100;
            }
        }
        return { currency, thresholdCents };
    }

    calculateProgress(cart) {
        const cartTotalPrice = parseInt(cart.total_price);
        const info = this.getCurrencyInfo(cart);
        this.currency = info.currency;

        if (!info.thresholdCents) {
            this.style.display = 'none';
            return;
        }
        this.style.display = 'block';

        const thresholdInCents = info.thresholdCents;

        let percentage = Math.round((cartTotalPrice * 100) / thresholdInCents);
        if (percentage >= 100) {
            percentage = 100;
        }

        const text = this.getText(cartTotalPrice, thresholdInCents, percentage);
        const classLabel = this.getClassLabel(percentage);

        this.setProgressWidthAndText(percentage, text, classLabel);

        if (cart.items) {
            this.updateColorSuggestion(cart, cartTotalPrice > 0 && cartTotalPrice < thresholdInCents);
        }
    }

    getText(cartTotalPrice, thresholdInCents, percentage) {
        let text;

        if (cartTotalPrice === 0) {
            this.progressBar.classList.add('progress-hidden');
            text = this.needsMoreMessage.replace('$X', this.formatMoney(thresholdInCents));
        } else if (cartTotalPrice >= thresholdInCents) {
            this.progressBar.classList.remove('progress-hidden');
            this.freeShippingEligible = 1;
            text = this.achievedMessage;
        } else {
            this.progressBar.classList.remove('progress-hidden');
            const remainingPrice = thresholdInCents - cartTotalPrice;
            text = this.needsMoreMessage.replace('$X', this.formatMoney(remainingPrice));
            this.shipVal = "TBD";
        }

        return text;
    }

    // Amount (cents of the customer's currency) with the right symbol, rounded UP to a whole unit.
    // In the shop currency it is wrapped in a "money" span so Bucks can convert it.
    formatMoney(cents) {
        const amount = Math.ceil(cents / 100);
        let formatted;
        try {
            formatted = new Intl.NumberFormat(document.documentElement.lang || undefined, {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(amount);
        } catch (e) {
            formatted = String(amount);
        }

        if (this.currency === this.baseCurrency) {
            return '<span class="money">' + formatted + '</span>';
        }
        return formatted;
    }

    getClassLabel(percentage) {
        let classLabel;

        if (percentage === 0) {
            classLabel = 'progress-0';
        } else if (percentage <= 30) {
            classLabel = 'progress-30';
        } else if (percentage <= 60) {
            classLabel = 'progress-60';
        } else if (percentage < 100) {
            classLabel = 'progress-90';
        } else {
            classLabel = 'progress-100';
        }

        return classLabel;
    }

    resetProgressClass(classLabel) {
        this.progressBar.classList.remove('progress-0');
        this.progressBar.classList.remove('progress-30');
        this.progressBar.classList.remove('progress-60');
        this.progressBar.classList.remove('progress-90');
        this.progressBar.classList.remove('progress-100');
        this.progressBar.classList.add(classLabel);
    }

    setProgressWidthAndText(percentage, text, classLabel) {
        setTimeout(() => {
            this.resetProgressClass(classLabel);

            if (this.progressMeter) {
                this.progressMeter.style.width = `${percentage}%`;
            }

            if (this.textEnabled) {
                const textElement = this.progressMeter?.querySelector('.text');
                if (textElement) {
                    textElement.innerHTML = `${percentage}%`;
                }
            }

            if (this.messageElement && this.messageElement.innerHTML !== text) {
                this.messageElement.innerHTML = text;
            }

            // Shop currency only: ask Bucks to convert the new amount (safe to repeat).
            if (this.currency === this.baseCurrency) {
                try {
                    if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') {
                        window.bucksCC.reConvert();
                    }
                } catch (e) {}
            }

            FreeShippingMeter.injectSavings();
        }, 30);
    }

    // ---- "Add another color": other colors of the last added item, shown with the item's photo in
    // that color. All colors sit on ONE row (two rows only when there are more than 4). One tap
    // adds it to the cart and refreshes the drawer. ----
    updateColorSuggestion(cart, show) {
        if (!this.suggestBox) {
            this.suggestBox = document.createElement('div');
            this.suggestBox.className = 'ja-color-suggest';
            this.suggestBox.style.cssText = 'margin:0.4rem 0 0;font-size:1.1rem;line-height:1.3;text-align:center;';
            this.suggestBox.addEventListener('click', (e) => {
                const b = e.target.closest('[data-add-color]');
                if (b) this.addColor(b);
            });
            this.appendChild(this.suggestBox);
        }
        const box = this.suggestBox;
        const token = (this.suggestToken = (this.suggestToken || 0) + 1);
        const clear = () => { box.innerHTML = ''; this.style.paddingBottom = ''; };
        if (!show || !cart.items || !cart.items.length) {
            clear();
            return;
        }
        const item = cart.items[0];
        FreeShippingMeter.getProduct(item.handle).then((p) => {
            if (token !== this.suggestToken) return;
            if (!p) { clear(); return; }
            const ci = p.options.findIndex((o) => /colou?r/i.test(typeof o === 'string' ? o : o.name));
            if (ci < 0) { clear(); return; }
            const cur = item.variant_options || [];
            const colors = {};
            p.variants.forEach((v) => {
                if (!v.available) return;
                const c = v['option' + (ci + 1)];
                if (!c || c === cur[ci]) return;
                const same = [0, 1, 2].every((i) => i === ci || !cur[i] || v['option' + (i + 1)] === cur[i]);
                if (!colors[c] || (same && !colors[c].same)) {
                    const img = (v.featured_image && v.featured_image.src) || '';
                    colors[c] = { id: v.id, same: same, img: img.replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2') };
                }
            });
            const names = Object.keys(colors).slice(0, 8);
            if (!names.length) { clear(); return; }
            const cols = names.length <= 4 ? names.length : Math.ceil(names.length / 2);
            this.style.paddingBottom = '0.3rem';
            let html = '<span style="display:block;margin-bottom:0.4rem;">Add another color to get there faster:</span><div style="display:grid;grid-template-columns:repeat(' + cols + ',minmax(0,1fr));gap:0.4rem;">';
            names.forEach((n) => {
                const safe = n.replace(/</g, '&lt;').replace(/"/g, '&quot;');
                html += '<button type="button" data-add-color="' + colors[n].id + '" style="display:flex;align-items:center;justify-content:center;gap:0.35rem;min-width:0;padding:0.2rem 0.5rem 0.2rem 0.2rem;border:1px solid currentColor;border-radius:999px;background:transparent;color:inherit;font:inherit;font-size:1.05rem;line-height:1.1;cursor:pointer;touch-action:manipulation;">' +
                    (colors[n].img ? '<img src="' + colors[n].img + '" alt="" width="22" height="22" style="width:2.2rem;height:2.2rem;border-radius:50%;object-fit:cover;flex-shrink:0;">' : '') +
                    '<span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + safe + '</span></button>';
            });
            box.innerHTML = html + '</div>';
        });
    }

    addColor(btn) {
        if (btn.disabled) return;
        const id = parseInt(btn.getAttribute('data-add-color'), 10);
        btn.disabled = true;
        btn.style.opacity = '0.5';
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer && typeof drawer.getSectionsToRender === 'function'
            ? drawer.getSectionsToRender().map((s) => s.section || s.id).join(',') : '';
        const url = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: id, quantity: 1 }], sections: sections, sections_url: window.location.pathname })
        })
            .then((r) => r.json().then((j) => ({ ok: r.ok, json: j })))
            .then((res) => {
                if (!res.ok) throw new Error('add failed');
                if (drawer && typeof drawer.renderContents === 'function' && res.json.sections) {
                    drawer.renderContents(res.json);
                } else {
                    window.location.reload();
                }
            })
            .catch(() => {
                btn.disabled = false;
                btn.style.opacity = '';
            });
    }

    static getProduct(handle) {
        FreeShippingMeter.productCache = FreeShippingMeter.productCache || {};
        if (!FreeShippingMeter.productCache[handle]) {
            FreeShippingMeter.productCache[handle] = fetch('/products/' + handle + '.js')
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null);
        }
        return FreeShippingMeter.productCache[handle];
    }

    // ---- "You save $X" under the item's price (the line total on the right) ----
    // The cart snippet lists each saving (already in the customer's currency) in a hidden
    // [data-cart-savings] block, keyed by line number.
    static injectSavings() {
        const holder = document.querySelector('[data-cart-savings]');
        const saved = {};
        if (holder) {
            holder.querySelectorAll('[data-line]').forEach((el) => {
                saved[el.getAttribute('data-line')] = el.innerHTML;
            });
        }
        document.querySelectorAll('[id^="CartDrawer-Item-"], [id^="CartItem-"]').forEach((row) => {
            const line = row.id.replace(/^\D+/, '');
            let existing = row.querySelector('.cart-item__savings');
            const html = saved[line];
            if (!html) {
                if (existing) existing.remove();
                return;
            }
            const wrappers = row.querySelectorAll('.cart-item__price-wrapper');
            const dest = row.querySelector('.cart-item__totals .cart-item__price-wrapper') ||
                (wrappers.length ? wrappers[wrappers.length - 1] : null) ||
                row.querySelector('.cart-item__totals') || row.querySelector('.cart-item__details') || row;
            if (!existing) {
                existing = document.createElement('p');
                existing.className = 'cart-item__savings';
                existing.style.cssText = 'display:block;width:100%;flex-basis:100%;margin:0.3rem 0 0;text-align:inherit;';
            }
            if (existing.innerHTML !== 'You save ' + html) existing.innerHTML = 'You save ' + html;
            if (existing.parentNode !== dest || dest.lastElementChild !== existing) dest.appendChild(existing);
        });
        FreeShippingMeter.ensureTrustStrip();
    }

    // ---- Trust strip in the cart drawer footer, just above the total price: ONE short line,
    // icon next to its text, three items side by side. ----
    static ensureTrustStrip() {
        const footer = document.querySelector('.cart-drawer__footer');
        if (!footer || footer.querySelector('.ja-cart-trust')) return;
        if (!window.jaShip) {
            if (!FreeShippingMeter.loadingTokens) {
                FreeShippingMeter.loadingTokens = true;
                const me = document.querySelector('script[src*="free-shipping.js"]');
                if (me) {
                    const s = document.createElement('script');
                    s.src = me.src.replace(/free-shipping\.js/, 'shipping-amount-tokens.js');
                    s.onload = () => FreeShippingMeter.ensureTrustStrip();
                    document.head.appendChild(s);
                }
            }
            return;
        }
        const l = window.jaShip.lines();
        // Shortened for one line: "$4.99 US Delivery", "Get it by Oct 21", "Free exchanges".
        const short = (s, re, rep) => (s ? s.replace(re, rep).trim() : s);
        const rows = [
            [short(l.delivery, /\s*[·|\-].*$/, ''), '<rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle>'],
            [short(l.earliest, /^get it as soon as\s*/i, 'Get it by '), '<polyline points="20 6 9 17 4 12"></polyline>'],
            [short(l.exchange, /\s+within.*$/i, ''), '<polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>']
        ].filter((r) => r[0]);
        if (!rows.length) return;
        const box = document.createElement('div');
        box.className = 'ja-cart-trust';
        box.style.cssText = 'margin:0 0 0.8rem;padding:0.6rem 0.6rem;border:1px solid #e8e0d5;border-radius:8px;background:#fdfbf7;display:flex;flex-wrap:nowrap;align-items:center;justify-content:space-between;gap:0.4rem;overflow:hidden;';
        box.innerHTML = rows.map((r) =>
            '<div style="display:flex;flex-direction:row;align-items:center;gap:0.35rem;flex:0 1 auto;min-width:0;white-space:nowrap;font-size:1rem;font-weight:600;line-height:1.2;color:#2c2c2c;">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="width:1.2rem;height:1.2rem;flex-shrink:0;">' + r[1] + '</svg>' +
            '<span style="overflow:hidden;text-overflow:ellipsis;">' + r[0].replace(/</g, '&lt;') + '</span></div>'
        ).join('');
        // Above the total price: before the totals row when there is one, otherwise at the top
        // of the footer.
        const totals = footer.querySelector('.totals');
        if (totals && totals.parentNode) {
            totals.parentNode.insertBefore(box, totals);
        } else {
            footer.insertBefore(box, footer.firstChild);
        }
    }

    // Watches only the cart drawer (not the whole page) so the rest of the site is not slowed down.
    static watchSavings() {
        if (FreeShippingMeter.watching) return;
        FreeShippingMeter.watching = true;
        let timer = null;
        const root = document.querySelector('cart-drawer') || document.body;
        new MutationObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(FreeShippingMeter.injectSavings, 80);
        }).observe(root, { childList: true, subtree: true });
    }
}

customElements.define('free-shipping-component', FreeShippingMeter);

// "You May Also Like" cards in the cart drawer: the color / size dropdowns pick the variant that
// the card's Add To Cart button adds, and update its price.
if (!window.__jaUpgradeOpts) {
    window.__jaUpgradeOpts = true;
    document.addEventListener('change', function (e) {
        const sel = e.target && e.target.closest ? e.target.closest('.upgrade-opt') : null;
        if (!sel) return;
        const card = sel.closest('.upgrade-product-card');
        if (!card || !card.dataset.variants) return;
        let variants;
        try { variants = JSON.parse(card.dataset.variants); } catch (err) { return; }
        const selects = Array.prototype.slice.call(card.querySelectorAll('.upgrade-opt'));
        const idx = parseInt(sel.dataset.opt, 10);
        let wanted = selects.map((s) => s.value);
        const match = (arr) => variants.find((v) => arr.every((val, i) => v.o[i] === val));
        let v = match(wanted);
        if (!v) {
            // That exact combination does not exist: take the first one with the chosen value.
            v = variants.find((x) => x.o[idx] === sel.value && x.a) || variants.find((x) => x.o[idx] === sel.value);
            if (v) selects.forEach((s, i) => { if (v.o[i] !== undefined) s.value = v.o[i]; });
        }
        if (!v) return;
        const btn = card.querySelector('.upgrade-add-btn');
        if (btn) {
            btn.dataset.variantId = v.id;
            btn.disabled = !v.a;
            btn.textContent = v.a ? 'Add To Cart' : 'Sold Out';
        }
        const cur = card.querySelector('.upgrade-price-current');
        const cmp = card.querySelector('.upgrade-price-compare');
        if (cur) cur.textContent = v.p;
        if (cmp) {
            cmp.textContent = v.c || '';
            cmp.hidden = !v.c;
        }
    });
}
